import {useEffect, useMemo} from 'react'
import ContainerLayout from '../../components/layout/ContainerLayout'
import {CSVLink} from 'react-csv'
import WizButton from '../../components/button/WizButton'
import {Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts'
import Text from '../../components/text/Text'
import {Data, Headers, LabelKeyObject} from 'react-csv/lib/core'
import useQueryDataStat from '../../hooks/api/analysis/useQueryDataStat'
import moment from 'moment'

interface CDataSet {
    name: string
    timestamp: number
    spot_size: number
    long_size: number
    short_size: number
    buy_vol: number
    sell_vol: number
    long_vol: number
    short_vol: number
    price: number
}

interface IComposeChartProps {
    title?: string
    dataset?: CDataSet[]
    headers?: Headers
    csvData?: any
}

interface IProps {
    className?: string
}

const DataStat: React.FC<IProps> = ({className}) => {
    const {data, refetch} = useQueryDataStat()

    const {
        dataset = [],
        title = '',
        headers = [],
        csvData = [],
    } = useMemo<IComposeChartProps>(() => {
        const dataset: CDataSet[] = []
        const headers: LabelKeyObject[] = []
        const csvData: Data = []
        if (data === undefined) {
            return {
                dataset,
                headers,
                csvData,
                title: 'Data sample',
            }
        }
        Object.keys(data[0]).forEach(k => {
            headers.push({
                label: k,
                key: k,
            })
        })

        for (let key in data) {
            let date = ''

            let row = {}
            Object.keys(data[key]).forEach(k => {
                row[k] = data[key][k]
                if (typeof data[key][k] === 'number') {
                    data[key][k] = parseFloat(data[key][k].toFixed(4))
                }

                if (k === 'timestamp') {
                    date = moment.unix(data[key][k]).format('MM/DD HH:mm')
                }
            })

            let set: CDataSet = {
                ...data[key],
                name: date,
            }

            csvData.push(set)
            dataset.push(set)
        }

        return {dataset, title: 'tamp_data', headers, csvData}
    }, [data])

    useEffect(() => {
        refetch()
    }, [refetch])

    return (
        <ContainerLayout title={'tamp data chart'} className={'block'}>
            <div className="flex justify-between w-full">
                <div className="flex gap-[10px] justify-end">
                    <CSVLink data={csvData} headers={headers} filename={`tamp_data.csv`}>
                        <WizButton text={'export csv'} className="mb-5 mr-5" />
                    </CSVLink>
                </div>
            </div>

            <div className="z-0 flex flex-col">
                <div className="grid grid-cols-2 w-full h-full p-[30px] border-double border-2 border-grey-500">
                    <div className="relative">
                        {dataset.length !== 0 ? (
                            <>
                                <Text children={`Data chart`} className="m-[1rem]" />
                                <ResponsiveContainer width={5000} height={800}>
                                    <ComposedChart syncId={title} width={5000} height={800} data={dataset}>
                                        <XAxis dataKey="name" />
                                        <YAxis
                                            yAxisId="right"
                                            stroke="#5cd65c"
                                            label={{
                                                offset: 5,
                                                value: 'size',
                                                fill: '#5cd65c',
                                                angle: 90,
                                                position: 'center',
                                            }}
                                            orientation="right"
                                        />
                                        <YAxis
                                            stroke="#ff7300"
                                            label={{
                                                fill: '#ff7300',
                                                value: 'Bitcoin',
                                                angle: -90,
                                            }}
                                            yAxisId="left"
                                            domain={[32000, 'dataMax+1000']}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <CartesianGrid stroke="#f5f5f5" />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="short_size"
                                            fill="#a0413e"
                                            stroke="#a0413e"
                                        />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="long_size"
                                            fill="#5cd65c"
                                            stroke="#5cd65c"
                                        />
                                        <Line
                                            dot={<div></div>}
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#ff7300"
                                            strokeWidth={3}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                                <Text children={`Data chart`} className="m-[1rem]" />
                                <ResponsiveContainer width={5000} height={800}>
                                    <ComposedChart syncId={title} width={5000} height={800} data={dataset}>
                                        <XAxis dataKey="name" />
                                        <YAxis
                                            yAxisId="right"
                                            stroke="#5cd65c"
                                            label={{
                                                offset: 5,
                                                value: 'volume',
                                                fill: '#5cd65c',
                                                angle: 90,
                                                position: 'center',
                                            }}
                                            orientation="right"
                                            domain={[0, 'dataMax+1000']}
                                        />
                                        <YAxis
                                            stroke="#ff7300"
                                            label={{
                                                fill: '#ff7300',
                                                value: 'Bitcoin',
                                                angle: -90,
                                            }}
                                            yAxisId="left"
                                            domain={[32000, 'dataMax+1000']}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <CartesianGrid stroke="#f5f5f5" />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="sell_vol"
                                            fill="#a0413e"
                                            stroke="#a0413e"
                                        />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="buy_vol"
                                            fill="#5cd65c"
                                            stroke="#5cd65c"
                                        />
                                        <Line
                                            dot={<div></div>}
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#ff7300"
                                            strokeWidth={3}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                                <Text children={`Data chart`} className="m-[1rem]" />
                                <ResponsiveContainer width={5000} height={800}>
                                    <ComposedChart syncId={title} width={5000} height={800} data={dataset}>
                                        <XAxis dataKey="name" />
                                        <YAxis
                                            yAxisId="right"
                                            stroke="#5cd65c"
                                            label={{
                                                offset: 5,
                                                value: 'volume',
                                                fill: '#5cd65c',
                                                angle: 90,
                                                position: 'center',
                                            }}
                                            orientation="right"
                                            domain={[0, 'dataMax+1000']}
                                        />
                                        <YAxis
                                            stroke="#ff7300"
                                            label={{
                                                fill: '#ff7300',
                                                value: 'Bitcoin',
                                                angle: -90,
                                            }}
                                            yAxisId="left"
                                            domain={[32000, 'dataMax+1000']}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <CartesianGrid stroke="#f5f5f5" />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="short_vol"
                                            fill="#a0413e"
                                            stroke="#a0413e"
                                        />
                                        <Area
                                            type="monotone"
                                            yAxisId="right"
                                            dataKey="long_vol"
                                            fill="#5cd65c"
                                            stroke="#5cd65c"
                                        />
                                        <Line
                                            dot={<div></div>}
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#ff7300"
                                            strokeWidth={3}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                                <Text children={`Data chart`} className="m-[1rem]" />
                                <ResponsiveContainer width={5000} height={800}>
                                    <ComposedChart syncId={title} width={5000} height={800} data={dataset}>
                                        <XAxis dataKey="name" />
                                        <YAxis
                                            yAxisId="right"
                                            stroke="#5cd65c"
                                            label={{
                                                offset: 5,
                                                value: 'volume',
                                                fill: '#5cd65c',
                                                angle: 90,
                                                position: 'center',
                                            }}
                                            orientation="right"
                                            domain={[0, 'dataMax+1000']}
                                        />
                                        <YAxis
                                            stroke="#ff7300"
                                            label={{
                                                fill: '#ff7300',
                                                value: 'Bitcoin',
                                                angle: -90,
                                            }}
                                            yAxisId="left"
                                            domain={[32000, 'dataMax+1000']}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <CartesianGrid stroke="#f5f5f5" />
                                        <Area
                                            yAxisId="right"
                                            name="spot_size"
                                            type="monotone"
                                            dataKey="spot_size"
                                            fill="#8884d8"
                                            stroke="#8884d8"
                                        />
                                        <Line
                                            dot={<div></div>}
                                            yAxisId="left"
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#ff7300"
                                            strokeWidth={3}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
        </ContainerLayout>
    )
}

export default DataStat
